import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

import { User } from "../../../api";
import { useAuth } from "../../../hooks/useAuth";
import { usePermissions } from "../../../hooks/usePermissions";
import { ACTIONS, ENV } from "../../../utils/constants";

import styles from "./Users.module.scss";

const LIMIT_OPTIONS = [10, 20, 50];

function getUserFullName(user) {
  return [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim();
}

function getUserAvatar(user) {
  const avatarPath = user?.avatar;

  if (!avatarPath) {
    return "";
  }

  if (avatarPath.startsWith("http://") || avatarPath.startsWith("https://")) {
    return avatarPath;
  }

  const normalizedPath = avatarPath.replace(/\\/g, "/").replace(/^\/+/, "");

  return `${ENV.SERVER_HOST}/${normalizedPath}`;
}

function getUserRoleNames(user) {
  if (!Array.isArray(user?.roles)) {
    return [];
  }

  return user.roles
    .map((role) => (typeof role === "object" ? role.name : ""))
    .filter(Boolean);
}

function getRoleBadgeClassName(roleName) {
  return String(roleName || "").trim().toLowerCase() === "admin"
    ? styles.RoleBadgeAdmin
    : styles.RoleBadge;
}

function formatDate(date) {
  if (!date) {
    return "—";
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("es-GT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsedDate);
}

function getVisiblePages(currentPage, totalPages) {
  const pages = [];
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
}

function getPaginationResponse(result, page, limit) {
  const response = result?.users || result?.response || result || {};

  const docs = Array.isArray(response.docs)
    ? response.docs
    : Array.isArray(response)
      ? response
      : [];

  const totalDocs =
    Number(response.totalDocs) ||
    Number(response.total) ||
    Number(response.totalItems) ||
    docs.length;

  const totalPages =
    Number(response.totalPages) ||
    Number(response.pages) ||
    Math.max(1, Math.ceil(totalDocs / limit));

  return {
    docs,
    page: Number(response.page) || page,
    limit: Number(response.limit) || limit,
    totalPages,
    totalDocs,
  };
}

export function UsersPage() {
  const { hasAction } = usePermissions();
  const canAdd = hasAction("users", ACTIONS.ADD);
  const canEdit = hasAction("users", ACTIONS.MOD);
  const canDelete = hasAction("users", ACTIONS.DEL);
  // Activar/desactivar una cuenta es un permiso propio, separado de
  // "editar" (MOD) -- alguien puede tener permiso para corregir el
  // nombre/correo de un usuario sin necesariamente poder suspender su
  // acceso al panel, o viceversa.
  const canActivate = hasAction("users", ACTIONS.ACTIVATE);

  const location = useLocation();
  const actionMenuRef = useRef(null);
  const actionMenuPanelRef = useRef(null);
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isUpdatingActive, setIsUpdatingActive] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalDocs: 0,
  });

  const [searchValue, setSearchValue] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.success || ""
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadUsers = useCallback(
    async (page = 1, limit = pagination.limit, search = searchValue) => {
      try {
        setIsLoading(true);
        setError("");
        setOpenActionMenu(null);

        const userCtrl = new User();

        const filters = {};

        if (search.trim()) {
          filters.search = search.trim();
        }

        const result = await userCtrl.getUsers(page, limit, filters);
        const response = getPaginationResponse(result, page, limit);

        setUsers(response.docs);

        setPagination({
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
          totalDocs: response.totalDocs,
        });
      } catch (errorResponse) {
        console.error("loadUsers error:", errorResponse);

        setUsers([]);

        setPagination({
          page: 1,
          limit: pagination.limit,
          totalPages: 1,
          totalDocs: 0,
        });

        setError(
          errorResponse?.msg ||
            "No fue posible cargar los usuarios. Intenta nuevamente."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [pagination.limit, searchValue]
  );

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    function handlePointerDown(event) {
      const clickedTrigger = actionMenuRef.current?.contains(event.target);
      const clickedPanel = actionMenuPanelRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPanel) {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (openActionMenu === null) {
      return undefined;
    }

    function handleReposition() {
      setOpenActionMenu(null);
      setActionMenuPosition(null);
    }

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [openActionMenu]);

  function handleSearchSubmit(event) {
    event.preventDefault();

    loadUsers(1, pagination.limit, searchValue);
  }

  function handleClearSearch() {
    setSearchValue("");
    loadUsers(1, pagination.limit, "");
  }

  function handlePageChange(page) {
    if (page < 1 || page > pagination.totalPages || page === pagination.page) {
      return;
    }

    loadUsers(page);
  }

  function handleLimitChange(event) {
    const nextLimit = Number(event.target.value);

    loadUsers(1, nextLimit);
  }

  function handleOpenDeleteModal(user) {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
    setUserToDelete(user);
    setError("");
  }

  function handleCloseDeleteModal() {
    if (isDeleting) {
      return;
    }

    setUserToDelete(null);
  }

  async function handleConfirmDelete() {
    if (!userToDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      setError("");

      const userCtrl = new User();

      await userCtrl.deleteUser(userToDelete._id);

      setSuccessMessage("Usuario eliminado correctamente.");
      setUserToDelete(null);

      const nextPage =
        users.length === 1 && pagination.page > 1
          ? pagination.page - 1
          : pagination.page;

      await loadUsers(nextPage);
    } catch (errorResponse) {
      console.error("handleConfirmDelete error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible eliminar el usuario. Intenta nuevamente."
      );
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleChangeActive(user) {
    if (isUpdatingActive) {
      return;
    }

    try {
      setIsUpdatingActive(true);
      setUpdatingUserId(user._id);
      setError("");

      const userCtrl = new User();

      await userCtrl.updateUser(user._id, {
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        role: user.role,
        active: !user.active,
      });

      setSuccessMessage(
        user.active ? "Usuario desactivado." : "Usuario activado."
      );

      await loadUsers(pagination.page);
    } catch (errorResponse) {
      console.error("handleChangeActive error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible actualizar el estado del usuario."
      );
    } finally {
      setIsUpdatingActive(false);
      setUpdatingUserId(null);
    }
  }

  const visiblePages = getVisiblePages(pagination.page, pagination.totalPages);

  const firstUserNumber =
    pagination.totalDocs === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;

  const lastUserNumber = Math.min(
    pagination.page * pagination.limit,
    pagination.totalDocs
  );

  return (
    <section className={styles.UsersPage}>
      <div className={styles.TopBar}>
        <form className={styles.SearchForm} onSubmit={handleSearchSubmit}>
          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Buscar por nombre, apellido o correo..."
            aria-label="Buscar usuarios"
          />

          <button type="submit" aria-label="Buscar usuarios">
            ⌕
          </button>
        </form>

        <div className={styles.TopActions}>
          {canAdd && (
            <Link to="/admin/users/create" className={styles.CreateButton}>
              <span>＋</span>
              Nuevo usuario
            </Link>
          )}
        </div>
      </div>

      {searchValue.trim() && (
        <div className={styles.Filters}>
          <span className={styles.ActiveSearch}>
            Búsqueda: <strong>{searchValue.trim()}</strong>
          </span>

          <button
            type="button"
            className={styles.ClearButton}
            onClick={handleClearSearch}
          >
            Limpiar búsqueda
          </button>
        </div>
      )}

      {successMessage && (
        <div className={styles.SuccessMessage} role="status">
          {successMessage}
        </div>
      )}

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className={styles.StateMessage}>Cargando usuarios...</div>
      ) : users.length === 0 ? (
        <div className={styles.EmptyState}>
          <p>No hay usuarios para mostrar.</p>
        </div>
      ) : (
        <>
          <p className={styles.TableInfo}>
            Mostrando {firstUserNumber} a {lastUserNumber} de{" "}
            {pagination.totalDocs} usuarios
          </p>

          <div className={styles.TableContainer}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Correo</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Fecha creación</th>
                  <th className={styles.ActionsColumn}>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {users.map((user) => {
                  const userAvatar = getUserAvatar(user);
                  const userRoleNames = getUserRoleNames(user);

                  return (
                    <tr key={user._id}>
                      <td>
                        <div className={styles.UserCell}>
                          {userAvatar ? (
                            <img
                              className={styles.UserAvatar}
                              src={userAvatar}
                              alt=""
                            />
                          ) : (
                            <span className={styles.UserAvatarPlaceholder}>
                              {(user.firstname || "?").charAt(0).toUpperCase()}
                            </span>
                          )}

                          <p
                            className={styles.UserName}
                            title={getUserFullName(user) || "Sin nombre"}
                          >
                            {getUserFullName(user) || "Sin nombre"}
                          </p>
                        </div>
                      </td>

                      <td className={styles.EmailCell} title={user.email}>
                        {user.email}
                      </td>

                      <td>
                        {userRoleNames.length > 0 ? (
                          <div
                            className={styles.RoleBadgeList}
                            title={userRoleNames.join(", ")}
                          >
                            {userRoleNames.map((roleName) => (
                              <span
                                key={roleName}
                                className={getRoleBadgeClassName(roleName)}
                              >
                                {roleName}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={styles.RoleBadgeNone}>
                            Sin rol asignado
                          </span>
                        )}
                      </td>

                      <td>
                        <label
                          className={styles.ActiveSwitch}
                          title={
                            user.active
                              ? "Desactivar usuario"
                              : "Activar usuario"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={Boolean(user.active)}
                            onChange={() => handleChangeActive(user)}
                            disabled={
                              !canActivate ||
                              String(user._id) === String(currentUser?._id) ||
                              (isUpdatingActive && updatingUserId === user._id)
                            }
                          />

                          <span />
                        </label>
                      </td>

                      <td className={styles.DateCell}>
                        {formatDate(user.createdate || user.createDate)}
                      </td>

                      <td className={styles.ActionsColumn}>
                        {(canEdit || canDelete) && (
                        <div
                          ref={
                            openActionMenu === user._id ? actionMenuRef : null
                          }
                          className={styles.RowActions}
                        >
                          <button
                            type="button"
                            className={styles.MenuButton}
                            aria-label={`Acciones para ${getUserFullName(
                              user
                            )}`}
                            aria-expanded={openActionMenu === user._id}
                            aria-haspopup="menu"
                            onClick={(event) => {
                              if (openActionMenu === user._id) {
                                setOpenActionMenu(null);
                                setActionMenuPosition(null);
                                return;
                              }

                              const rect =
                                event.currentTarget.getBoundingClientRect();
                              const menuWidth = 210;
                              const viewportRight =
                                document.documentElement.clientWidth;
                              const left = Math.min(
                                rect.right + window.scrollX - menuWidth,
                                viewportRight + window.scrollX - menuWidth - 8
                              );

                              setActionMenuPosition({
                                top: rect.bottom + window.scrollY + 6,
                                left: Math.max(left, 8),
                              });
                              setOpenActionMenu(user._id);
                            }}
                          >
                            ⋮
                          </button>

                          {openActionMenu === user._id &&
                            actionMenuPosition &&
                            createPortal(
                              <div
                                ref={actionMenuPanelRef}
                                className={styles.ActionMenu}
                                style={{
                                  position: "absolute",
                                  top: actionMenuPosition.top,
                                  left: actionMenuPosition.left,
                                }}
                                role="menu"
                              >
                                {canEdit && (
                                <Link
                                  to={`/admin/users/${user._id}/edit`}
                                  role="menuitem"
                                  onClick={() => {
                                    setOpenActionMenu(null);
                                    setActionMenuPosition(null);
                                  }}
                                >
                                  Editar usuario
                                </Link>
                                )}

                                {canDelete && (
                                <button
                                  type="button"
                                  className={styles.DeleteAction}
                                  role="menuitem"
                                  disabled={
                                    String(user._id) ===
                                    String(currentUser?._id)
                                  }
                                  onClick={() => handleOpenDeleteModal(user)}
                                >
                                  Eliminar usuario
                                </button>
                                )}
                              </div>,
                              document.body
                            )}
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <footer className={styles.Pagination}>
            <label className={styles.LimitControl}>
              <span>Mostrar:</span>

              <select value={pagination.limit} onChange={handleLimitChange}>
                {LIMIT_OPTIONS.map((limit) => (
                  <option key={limit} value={limit}>
                    {limit}
                  </option>
                ))}
              </select>
            </label>

            <div className={styles.PaginationButtons}>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={pagination.page <= 1}
                aria-label="Ir a la primera página"
              >
                «
              </button>

              <button
                type="button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                aria-label="Ir a la página anterior"
              >
                ‹
              </button>

              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={page === pagination.page ? styles.CurrentPage : ""}
                  aria-label={`Ir a la página ${page}`}
                  aria-current={page === pagination.page ? "page" : undefined}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                aria-label="Ir a la página siguiente"
              >
                ›
              </button>

              <button
                type="button"
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page >= pagination.totalPages}
                aria-label="Ir a la última página"
              >
                »
              </button>
            </div>
          </footer>
        </>
      )}

      {userToDelete && (
        <div className={styles.DeleteModalOverlay}>
          <div
            className={styles.DeleteModal}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-user-title"
            aria-describedby="delete-user-description"
          >
            <div className={styles.DeleteModalIcon}>!</div>

            <h2 id="delete-user-title">Eliminar usuario</h2>

            <p id="delete-user-description">
              ¿Deseas eliminar al usuario{" "}
              <strong>{getUserFullName(userToDelete)}</strong>?
            </p>

            <p className={styles.DeleteModalWarning}>
              El usuario perderá acceso al panel de administración y no podrá
              iniciar sesión nuevamente.
            </p>

            <div className={styles.DeleteModalActions}>
              <button
                type="button"
                className={styles.CancelDeleteButton}
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                autoFocus
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.ConfirmDeleteButton}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Eliminando..." : "Eliminar usuario"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
